// Libraries
import { Clipboard } from '@angular/cdk/clipboard';
import { inject, Injectable, OnDestroy, Renderer2, Signal, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { format, parse } from 'date-fns';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';

// Classes
import { Pop } from 'src/app/classes/shared/pop';


/**
 * Global tools that
 */
@Injectable({
    providedIn: 'root'
})

export class UiTool implements OnDestroy {
    // Services
    private readonly clipboard: Clipboard = inject(Clipboard);
    private readonly pop: Pop = inject(Pop);
    private readonly translate: TranslateService = inject(TranslateService);

    // Variables
    private mobileBreakpoint = 1025;
    private isMobileSubject = new BehaviorSubject<boolean>(screen.width < this.mobileBreakpoint);
    isMobile$ = this.isMobileSubject.asObservable().pipe(distinctUntilChanged());
    destroy$ = new Subject<void>();

    // Signals
    private _isMobileSignal: WritableSignal<boolean> = signal<boolean>(screen.width < this.mobileBreakpoint);
    readonly isMobileSignal: Signal<boolean> = this._isMobileSignal.asReadonly();

    constructor() {
        fromEvent(window, 'resize').pipe(
            map(() => screen.width < this.mobileBreakpoint),
            distinctUntilChanged(),
            startWith(screen.width < this.mobileBreakpoint),
            takeUntil(this.destroy$)
        ).subscribe(isMobile => {
            this.isMobileSubject.next(isMobile);
            this._isMobileSignal.set(isMobile);
        });
    }

    // Angular lifecycle hook
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isMobile(): boolean {
        return this.isMobileSignal();
    }

    copy(content: string) {
        this.clipboard.copy(content);
        this.pop.toast(this.translate.instant('form.copy_clipboard'), "", 2000, 'content_copy');
    }

    /**
     * Save the color for each date in the calendar based on the provided activity data
     */
    applyCalendarColors(datesColors: [string, string][]): void {
        const allCalendarCells = document.querySelectorAll<HTMLElement>('.mat-calendar-body-cell');
        const cellsMap = new Map<string, HTMLElement>();

        allCalendarCells.forEach(cell => {
            const ariaLabel = cell.getAttribute('aria-label');
            if (ariaLabel) {
                cellsMap.set(ariaLabel, cell);
            }

            const child = cell.firstChild as HTMLElement;
            if (child) {
                child.style.padding = '';
                child.style.borderRadius = '';
                child.classList.remove('red', 'orange', 'green');
                child.className = 'mat-calendar-body-cell-content mat-focus-indicator';
            }
        });

        datesColors.forEach(([dateStr, color]) => {
            const date = parse(dateStr, 'yyyy-MM-dd', new Date());
            const ariaLabel = format(date, 'dd/MM/yyyy');
            const element = cellsMap.get(ariaLabel);

            if (element) {
                const child = element.firstChild as HTMLElement;
                if (child) {
                    child.style.padding = '3px';
                    child.style.borderRadius = '50%';
                    child.className = `mat-calendar-body-cell-content mat-focus-indicator ${color}`;
                }
            }
        });
    }

    /**
     * Get data activity and transform it to a format usable for coloring calendar cells
     */
    processActivityToDatesColors(activity: any): [string, string][] {
        const activityDetails = activity?.details?.[0];

        if (!activityDetails?.tracks?.length) {
            return [];
        }

        const datesColors: [string, string][] = [];

        activityDetails.tracks.forEach((t: any) => {
            let color = 'red';
            if (t[1] > 100) color = 'orange';
            if (t[1] > 1000) color = 'green';

            datesColors.push([format(new Date(t[0]), 'yyyy-MM-dd'), color]);
        });

        return datesColors;
    }

    /**
     * Configure listeners for calendar navigation buttons to reapply colors after month change
     */
    setupCalendarNavigationListeners(
        renderer: Renderer2,
        checkDatesFn: () => void,
        unlistenFns: (() => void)[]
    ): void {
        const prevButton = document.querySelector('button.mat-calendar-previous-button');
        const nextButton = document.querySelector('button.mat-calendar-next-button');

        if (prevButton) {
            const unlistenPrev = renderer.listen(prevButton, 'click', () => {
                setTimeout(() => checkDatesFn(), 50);
            });
            
            unlistenFns.push(unlistenPrev);
        }

        if (nextButton) {
            const unlistenNext = renderer.listen(nextButton, 'click', () => {
                setTimeout(() => checkDatesFn(), 50);
            });

            unlistenFns.push(unlistenNext);
        }
    }
}

